import { useAuth0 } from '@auth0/auth0-react';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { VITE_NAMESPACE } from '@/utils/envs';

export interface ClaimContextProps {
  claims: Record<string, unknown> | null;
  isLoading: boolean;
  isValidFor: (permissions: Array<string> | string) => boolean;
}

const REFRESH_TOKEN_ERROR_KEY = 'refresh_token_error_count';
const MAX_REFRESH_TOKEN_ERRORS = 3;
const LOGOUT_PARAMS = { logoutParams: { returnTo: window.location.origin } };
const FATAL_ERRORS = [
  'consent_required',
  'login_required',
  'invalid_grant',
  '403',
  'unauthorized',
  'Forbidden',
];

function decodeBase64Url(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error))
    return error.response?.data?.error || error.response?.data?.error_description || '';
  if (error instanceof Error) return error.message;
  return String(error);
}

function extractNamespacedClaims(payload: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload)
      .filter(([key]) => key.startsWith(VITE_NAMESPACE))
      .map(([key, value]) => [key.replace(VITE_NAMESPACE, ''), value]),
  );
}

export function useClaimProvider(): ClaimContextProps {
  const { getAccessTokenSilently, logout, loginWithRedirect } = useAuth0();
  const [jwt, setJwt] = useState<string | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadJWT = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      if (token) {
        localStorage.removeItem(REFRESH_TOKEN_ERROR_KEY);
        setJwt(token);
      }
    } catch (error) {
      const msg = getErrorMessage(error);

      if (FATAL_ERRORS.some((e) => msg.includes(e))) {
        localStorage.removeItem(REFRESH_TOKEN_ERROR_KEY);
        logout(LOGOUT_PARAMS);
        return;
      }

      if (msg.includes('Missing Refresh Token')) {
        const count = parseInt(localStorage.getItem(REFRESH_TOKEN_ERROR_KEY) || '0', 10) + 1;
        localStorage.setItem(REFRESH_TOKEN_ERROR_KEY, count.toString());

        if (count >= MAX_REFRESH_TOKEN_ERRORS) {
          localStorage.removeItem(REFRESH_TOKEN_ERROR_KEY);
          logout(LOGOUT_PARAMS);
        } else {
          loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        }
        return;
      }

      localStorage.removeItem(REFRESH_TOKEN_ERROR_KEY);
      logout(LOGOUT_PARAMS);
    }
  }, [getAccessTokenSilently, logout, loginWithRedirect]);

  const calculateClaims = useCallback(() => {
    const parts = jwt?.split('.');
    if (!parts || parts.length !== 3) {
      setIsLoading(false);
      return;
    }
    try {
      const payload = JSON.parse(decodeBase64Url(parts[1]));
      setClaims(extractNamespacedClaims(payload));
    } catch (error) {
      console.error('Error parsing JWT payload:', error);
      setClaims(null);
    }
    setIsLoading(false);
  }, [jwt]);

  const isValidFor = useCallback(
    (restrictedFor: Array<string> | string) => {
      const permissions = (claims?.['permissions'] ?? []) as Array<string>;
      const required = typeof restrictedFor === 'string' ? [restrictedFor] : restrictedFor;
      return required.length === 0 || required.some((p) => permissions.includes(p));
    },
    [claims],
  );

  useEffect(() => {
    if (!jwt) loadJWT();
    else calculateClaims();
  }, [jwt, loadJWT, calculateClaims]);

  return { claims, isLoading, isValidFor };
}
